/**
 * THE CAPABILITY DEFINITION DOMAIN STATE.
 *
 * WHAT THIS IS
 *   FE-1 Stage Work Item §7.5: the DOMAIN STATE — "owned by the domain layer;
 *   derived ONLY from frozen contract semantics; deterministic given the same
 *   server state". It answers ONE question: how should the presentation
 *   understand this consumer state? It answers no question about the capability
 *   itself.
 *
 * WHY A DISCRIMINATED UNION AND NOT `data?` / `error?`
 *   A shape like `{ data?: Snapshot; error?: Error }` makes four different
 *   situations expressible by the same value — no data yet, no data at all, a
 *   failed read, and a successful read — and therefore makes them collapsible by
 *   accident. Every state below is its own type, `snapshot` is present on exactly
 *   one variant, and a fifth variant cannot be added without a compile error at
 *   every match site (`matchDomainState`).
 *
 * THE FOUR VARIANTS, AND WHY THERE ARE FOUR RATHER THAN THREE
 *
 *   DEFINITION_READABLE            the read plane returned a snapshot. The
 *                                  AVAILABLE contract status, read as
 *                                  "the definition is readable". It does NOT mean
 *                                  the capability is operationally available,
 *                                  ready, assigned or committed.
 *   DEFINITION_UNKNOWN             the read plane resolved and the subject's
 *                                  definition data is absent. A normal answer,
 *                                  NOT an error, NOT empty, NOT a default.
 *   DEFINITION_READ_UNAVAILABLE    a functioning reader could not supply a
 *                                  trustworthy response. NOT fabricated data, and
 *                                  NO implementation diagnostic.
 *   DEFINITION_READ_NOT_COMPLETED  the frontend never obtained a read outcome.
 *                                  This is the Phase A FRONTEND-CONDITION axis,
 *                                  which is NOT a contract state. It exists as a
 *                                  fourth variant because the consumer's
 *                                  observation type has four variants and a total
 *                                  projection must handle all four — and because
 *                                  folding a frontend fault into UNAVAILABLE
 *                                  would report a wiring fault as a reader
 *                                  failure, which is a different and false
 *                                  statement (FE-1 Stage Work Item §7.5).
 *
 * WHAT NO VARIANT CARRIES, DELIBERATELY
 *   No `ready`, `eligible`, `operational`, `authorized`, `executable`, `assigned`,
 *   `committed`, `online`, `health` or `riskScore` field. Those are not frozen
 *   contract facts, so a domain state carrying one would be inventing a backend
 *   fact. The assertions at the bottom of this file fail the typecheck if such a
 *   field is added to any variant.
 *
 * `AVAILABLE` IS NOT OPERATIONAL AVAILABILITY
 *   The frozen contract's own comment: AVAILABLE "means only that snapshot data
 *   was retrieved, never that the capability is operationally available or
 *   ready." That is why the readable variant is named for the READ
 *   ("definition-readable"), not for the capability.
 */

import {
  NON_AUTHORITY,
  assertNever,
  type Freshness,
  type FrontendCondition,
  type NonAuthority,
} from '../../../../shared/semantic/index';
import type { CapabilityDefinitionSnapshotV1 } from '../consumer';

/**
 * The domain state discriminator.
 *
 * These are DOMAIN names, not contract names, and they are deliberately not the
 * contract's own status strings: the domain state is a reading of the read result,
 * and giving it the contract's spelling would invite a reader to treat the domain
 * state as the contract status itself.
 */
export type CapabilityDefinitionDomainStateKind =
  | 'DEFINITION_READABLE'
  | 'DEFINITION_UNKNOWN'
  | 'DEFINITION_READ_UNAVAILABLE'
  | 'DEFINITION_READ_NOT_COMPLETED';

/**
 * The definition is readable: the read plane returned a snapshot for the subject.
 *
 * `snapshot` is the frozen eight-field type, carried as the consumer mirror
 * declares it. The domain does not re-model it and does not add a ninth field to
 * it. NOT a claim of operational availability.
 */
export interface DefinitionReadableState {
  readonly kind: 'DEFINITION_READABLE';
  readonly snapshot: CapabilityDefinitionSnapshotV1;
  /** Carried verbatim from the consumer observation. See `./projection.ts`. */
  readonly freshness: Freshness;
  /** Fixed on every variant; the domain cannot elevate it. */
  readonly authority: NonAuthority;
}

/**
 * The subject's definition data is absent. An answer, not a failure.
 *
 * `snapshot: null` is explicit rather than an absent property, so "no snapshot"
 * is a value a reader can check rather than a field that happens to be missing.
 */
export interface DefinitionUnknownState {
  readonly kind: 'DEFINITION_UNKNOWN';
  readonly snapshot: null;
  readonly freshness: Freshness;
  readonly authority: NonAuthority;
}

/**
 * A functioning reader could not supply a trustworthy response.
 *
 * Carries NO diagnostic slot: the frozen read seam publishes none, so there is
 * nowhere for one to be put even by accident.
 */
export interface DefinitionReadUnavailableState {
  readonly kind: 'DEFINITION_READ_UNAVAILABLE';
  readonly snapshot: null;
  readonly freshness: Freshness;
  readonly authority: NonAuthority;
}

/**
 * No read outcome was obtained. The frontend is in a condition, not the read plane.
 *
 * Carries NO `freshness`, matching the Phase A frontend-condition observation,
 * which omits the field: a statement about the frontend's own progress cannot
 * carry a caveat about an answer's currency, because there is no answer.
 */
export interface DefinitionReadNotCompletedState {
  readonly kind: 'DEFINITION_READ_NOT_COMPLETED';
  readonly snapshot: null;
  readonly condition: FrontendCondition;
  readonly authority: NonAuthority;
}

/** Everything the domain layer may conclude about a Capability Definition read. */
export type CapabilityDefinitionDomainState =
  | DefinitionReadableState
  | DefinitionUnknownState
  | DefinitionReadUnavailableState
  | DefinitionReadNotCompletedState;

/* ------------------------------------------------------------------------- *
 * Construction sites. One per variant, and no literal is written anywhere else.
 * ------------------------------------------------------------------------- */

/** The definition is readable. NOT a claim of operational availability. */
export function definitionReadable(
  snapshot: CapabilityDefinitionSnapshotV1,
  freshness: Freshness,
): DefinitionReadableState {
  return { kind: 'DEFINITION_READABLE', snapshot, freshness, authority: NON_AUTHORITY };
}

/** The subject's definition data is absent. NOT an error and NOT empty. */
export function definitionUnknown(freshness: Freshness): DefinitionUnknownState {
  return { kind: 'DEFINITION_UNKNOWN', snapshot: null, freshness, authority: NON_AUTHORITY };
}

/** The reader could not supply a trustworthy response. No data, no diagnostic. */
export function definitionReadUnavailable(freshness: Freshness): DefinitionReadUnavailableState {
  return {
    kind: 'DEFINITION_READ_UNAVAILABLE',
    snapshot: null,
    freshness,
    authority: NON_AUTHORITY,
  };
}

/** No read outcome was obtained. A frontend condition, never a contract outcome. */
export function definitionReadNotCompleted(
  condition: FrontendCondition,
): DefinitionReadNotCompletedState {
  return {
    kind: 'DEFINITION_READ_NOT_COMPLETED',
    snapshot: null,
    condition,
    authority: NON_AUTHORITY,
  };
}

/* ------------------------------------------------------------------------- *
 * Variant identification.
 *
 * `kind` is a plain string-literal discriminant on a non-branded object, so
 * `state.kind === 'DEFINITION_READABLE'` narrows on its own. The guards exist so
 * that call sites read as identification rather than as string comparison, and so
 * that the vocabulary has one place to change.
 *
 * `isDefinitionReadable` IS NOT A SUCCESS PREDICATE. It identifies a variant. It
 * does not say the capability is available, ready, assigned or committed —
 * `AVAILABLE` means only that snapshot data was retrieved.
 * ------------------------------------------------------------------------- */

export function isDefinitionReadable(
  state: CapabilityDefinitionDomainState,
): state is DefinitionReadableState {
  return state.kind === 'DEFINITION_READABLE';
}

export function isDefinitionUnknown(
  state: CapabilityDefinitionDomainState,
): state is DefinitionUnknownState {
  return state.kind === 'DEFINITION_UNKNOWN';
}

export function isDefinitionReadUnavailable(
  state: CapabilityDefinitionDomainState,
): state is DefinitionReadUnavailableState {
  return state.kind === 'DEFINITION_READ_UNAVAILABLE';
}

export function isDefinitionReadNotCompleted(
  state: CapabilityDefinitionDomainState,
): state is DefinitionReadNotCompletedState {
  return state.kind === 'DEFINITION_READ_NOT_COMPLETED';
}

/**
 * Total match over a domain state.
 *
 * All four handlers are required. Omitting one is a compile error, and there is no
 * `default` handler to absorb a variant, so a domain conclusion cannot be quietly
 * dropped by a component (FE-1 Stage Work Item §7.4: a component renders; the
 * domain layer concludes).
 */
export interface DomainStateHandlers<TResult> {
  readonly onReadable: (state: DefinitionReadableState) => TResult;
  readonly onUnknown: (state: DefinitionUnknownState) => TResult;
  readonly onReadUnavailable: (state: DefinitionReadUnavailableState) => TResult;
  readonly onReadNotCompleted: (state: DefinitionReadNotCompletedState) => TResult;
}

export function matchDomainState<TResult>(
  state: CapabilityDefinitionDomainState,
  handlers: DomainStateHandlers<TResult>,
): TResult {
  switch (state.kind) {
    case 'DEFINITION_READABLE':
      return handlers.onReadable(state);
    case 'DEFINITION_UNKNOWN':
      return handlers.onUnknown(state);
    case 'DEFINITION_READ_UNAVAILABLE':
      return handlers.onReadUnavailable(state);
    case 'DEFINITION_READ_NOT_COMPLETED':
      return handlers.onReadNotCompleted(state);
    default:
      // Unreachable while the union has exactly four members. Kept as a value
      // rather than a silent fallthrough: a fifth variant becomes a compile error
      // here instead of being absorbed.
      return assertNever(state, 'matchDomainState');
  }
}

/* ------------------------------------------------------------------------- *
 * Compile-time assertions of the domain state's shape.
 *
 * These emit no runtime code. A runtime test proves a property held on one input;
 * an assertion proves it holds for every input at every build.
 * ------------------------------------------------------------------------- */

/** Fails to compile unless `T` is exactly `true`. */
type AssertTrue<T extends true> = T;

/** `true` only when the two unions have exactly the same members. */
type IsExactly<TLeft, TRight> = [TLeft] extends [TRight]
  ? [TRight] extends [TLeft]
    ? true
    : false
  : false;

/** `true` only when the two types share no member. */
type IsDisjoint<TLeft, TRight> = [Extract<TLeft, TRight>] extends [never] ? true : false;

/** The union has exactly the four declared members and no fifth. */
export type DomainStateHasExactlyFourVariants = AssertTrue<
  IsExactly<CapabilityDefinitionDomainStateKind, CapabilityDefinitionDomainState['kind']>
>;

/**
 * `Available != Assigned`, `Definition != Readiness`, and no invented backend
 * fact — asserted at the field level: none of these words is a key of ANY variant,
 * so no domain state can carry one.
 */
type OverClaimingFieldNames =
  | 'ready'
  | 'readiness'
  | 'eligible'
  | 'operational'
  | 'authorized'
  | 'executable'
  | 'assigned'
  | 'assignment'
  | 'committed'
  | 'online'
  | 'health'
  | 'riskScore'
  | 'missionCount'
  | 'availability';

type EveryVariantFieldName =
  | keyof DefinitionReadableState
  | keyof DefinitionUnknownState
  | keyof DefinitionReadUnavailableState
  | keyof DefinitionReadNotCompletedState;

export type NoVariantCarriesAnInventedBackendFact = AssertTrue<
  IsDisjoint<EveryVariantFieldName, OverClaimingFieldNames>
>;

/** The readable variant carries the frozen eight-field snapshot type itself. */
export type ReadableStateCarriesTheMirrorSnapshotType = AssertTrue<
  IsExactly<DefinitionReadableState['snapshot'], CapabilityDefinitionSnapshotV1>
>;

/**
 * `Unknown != Error` at the state level: the UNKNOWN state and the frontend-fault
 * state are disjoint types, so one cannot be substituted for the other.
 */
export type UnknownStateIsNotTheNotCompletedState = AssertTrue<
  IsDisjoint<DefinitionUnknownState, DefinitionReadNotCompletedState>
>;

/** `Unavailable != Unknown`: the two failure-ish states stay two types. */
export type UnavailableStateIsNotTheUnknownState = AssertTrue<
  IsDisjoint<DefinitionReadUnavailableState, DefinitionUnknownState>
>;

/**
 * `Read Model != Authority`: every variant requires an authority, and the only
 * authority value is `NonAuthority`, which is not a boolean and has one member.
 */
export type EveryVariantRequiresNonAuthority = AssertTrue<
  IsExactly<CapabilityDefinitionDomainState['authority'], NonAuthority>
>;

/**
 * A snapshot is present on exactly one variant. Asserted as an exact key set, so
 * removing it from the readable variant fails the build too.
 */
export type SnapshotIsCarriedByTheReadableVariant = AssertTrue<
  IsExactly<keyof DefinitionReadableState, 'kind' | 'snapshot' | 'freshness' | 'authority'>
>;
